import { useCallback, useState } from 'react';

const read = <T extends string>(key: string, values: readonly T[], fallback: T): T => {
  try {
    const stored = localStorage.getItem(key);
    return values.find((value) => value === stored) ?? fallback;
  } catch {
    return fallback;
  }
};

/**
 * One of `values`, remembered in this browser under `key`. A stored value that is no longer one of
 * them reads as `fallback`. Storage failures are swallowed: losing the preference is acceptable,
 * refusing to switch is not.
 */
export function usePersistedChoice<T extends string>(
  key: string,
  values: readonly T[],
  fallback: T,
) {
  const [choice, setChoiceState] = useState<T>(() => read(key, values, fallback));

  const setChoice = useCallback(
    (next: T) => {
      setChoiceState(next);
      try {
        localStorage.setItem(key, next);
      } catch {
        // Preference not kept; the choice still changed for this visit.
      }
    },
    [key],
  );

  return [choice, setChoice] as const;
}
